import { useMemo } from "react";
import { GkvBillEditDto, StepDef } from "../types/interface";

export interface UseEditStepperReturn {
  /** Current active step index (0-3). */
  activeStep: number;
  /** Step definitions with completion status. */
  steps: StepDef[];
}

/**
 * Derives the edit stepper state from bill data.
 *
 * Steps:
 *  0 = Bills loaded (start)
 *  1 = All Rxs taxiert
 *  2 = All bills printed (have PDFs) AND prints are still current
 *  3 = All bills exported
 *
 * `isPrintCurrent` allows the caller to invalidate the print step
 * when Rxs are added/removed after PDFs were generated.
 */
export function useEditStepper(
  bills: GkvBillEditDto[],
  isPrintCurrent: boolean,
): UseEditStepperReturn {
  const { activeStep, steps } = useMemo(() => {
    if (bills.length === 0) {
      return {
        activeStep: 0,
        steps: buildSteps(false, false, false),
      };
    }

    const allRxs = bills.flatMap((b) => b.rxs);

    const allTaxiert = allRxs.length > 0 && allRxs.every((rx) => rx.isTaxieren);
    const allPrinted =
      isPrintCurrent && bills.every((b) => b.paperCenterReceipt !== null && b.billPdf !== null);
    const allExported = bills.every((b) => b.wasExported);

    let step = 0;
    if (allTaxiert) step = 1;
    if (allTaxiert && allPrinted) step = 2;
    if (allExported) step = 3;

    return {
      activeStep: step,
      steps: buildSteps(allTaxiert, allPrinted, allExported),
    };
  }, [bills, isPrintCurrent]);

  return { activeStep, steps };
}

function buildSteps(taxiert: boolean, printed: boolean, exported: boolean): StepDef[] {
  return [
    { label: "Taxieren", completed: taxiert },
    { label: "Drucken", completed: printed },
    { label: "Exportieren", completed: exported },
  ];
}
